import "./CreateAccount.scss";

import { ComponentProps, FC, FocusEvent, FormEvent, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import CreateAccountImg from "assets/images/registration/CreateAccount.svg";
import Button from "components/Button/Button";
import Checkbox from "components/Checkbox/Checkbox";
import DisclosureLink from "components/DisclosureLink/DisclosureLink";
import EmailInput from "components/EmailInput/EmailInput";
import FormError from "components/FormError/FormError";
import PasswordInput from "components/PasswordInput/PasswordInput";
import Text from "components/Text/Text";
import { defaultPasswordLength } from "config";
import { AnalyticsIds } from "config/analytics";
import { AppError } from "config/errors";
import { IFormInput } from "config/types";
import { IRegisterUserInput } from "gql/mutations/user";
import { Layout, useDecodedAccessToken } from "modules";
import {
  useAuthorizeUnidentifiedUser,
  useCreateApplicationReferral,
  useForm,
  useJourney,
  useModal,
} from "modules/hooks";
import formValidations from "modules/hooks/form/Validations";
import { useTranslation } from "modules/hooks/useTranslation";
import { useAppDispatch } from "store";
import { login } from "store/actions/authentication";
import { setModal } from "store/actions/modal";
import { requestPolicyNewBusiness } from "store/actions/policy";
import { registerUser } from "store/actions/user";
import { IResetValues } from "utils/form";

import { get } from "lodash-es";

const CreateAccount: FC = () => {
  const { t } = useTranslation();
  const createApplicationReferral = useCreateApplicationReferral();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { clientId } = useAuthorizeUnidentifiedUser();
  const [formError, setFormError] = useState("");
  const { nextStepCallback } = useJourney();
  const createAccountAnalyticsId = get(AnalyticsIds, "loginRegistration.createAccountSignUp", "");
  const decodedToken = useDecodedAccessToken();
  const [passwordFocused, setPasswordFocused] = useState(false);

  const initialInputData: Partial<IFormInput>[] = [
    {
      id: "firstName",
      value: "",
    },
    {
      id: "lastName",
      value: "",
    },
    {
      id: "email",
      value: "",
      required: true,
      validations: ["email"],
    },
    {
      id: "password",
      value: "",
      required: true,
      validations: [{ name: "validatePasswordLength", passwordLength: defaultPasswordLength }],
    },
    {
      id: "joinNewsletter",
      value: true,
    },
  ];

  const { form, inputs } = useForm(initialInputData, { showInputErrorMessages: true });

  const onHaveAccountClick = () => {
    dispatch(setModal("ExitRegistrationModal"));
  };

  const SubheaderElement = (
    <div className="create-account__subheader-wrapper">
      <Text
        tag="p3"
        className="create-account__subheader-el"
        text={t(
          "register_user.form.subheader",
          "Sign up to save your progress. It only takes a minute."
        )}
      />
      <Text
        tag="l4"
        className="create-account__subheader-el link"
        text={t("register_user.already_have_account", "I already have an account")}
        onClick={onHaveAccountClick}
      />
    </div>
  );

  const content = {
    header: t("register_user.form.header", "Time to create an account!"),
    subheaderElement: SubheaderElement,
    imgSrc: CreateAccountImg,
  };

  const resetForm = () => {
    const resetValues: IResetValues = {
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      joinNewsletter: true,
    };
    form.reset(resetValues);
  };

  const openModal = useModal({ resetForm });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault();
      if (!form.valid) {
        return;
      }
      form.setLoading(true);

      // get identified user id and account id from access token
      const identifiedUser = get(decodedToken, "user");
      const identifiedUserId = get(identifiedUser, "user_id");

      const password = get(inputs, ["password", "value"]) as string;
      const userInfo = {
        userId: identifiedUserId,
        firstName: get(inputs, ["firstName", "value"]) as string,
        lastName: get(inputs, ["lastName", "value"]) as string,
        emailAddress: get(inputs, ["email", "value"]) as string,
        password,
        passwordConfirmation: password,
        newsletterOptIn: get(inputs, ["joinNewsletter", "value"], false) as boolean,
      };

      const registerUserInput: IRegisterUserInput = {
        attributes: {
          ...userInfo,
        },
      };

      const registerResponse = await dispatch(registerUser(registerUserInput));
      const thunkError = AppError.fromThunk(registerResponse);

      if (thunkError?.code === "EMAIL_ALREADY_TAKEN") {
        openModal("ExistingAccountModal");
        return;
      } else if (thunkError) {
        throw new AppError(thunkError);
      }

      // // Silently login user before proceeding
      const loginResponse = await dispatch(
        login({ email: userInfo.emailAddress, password: userInfo.password, clientId })
      );

      const loginError = AppError.fromThunk(loginResponse);

      if (loginError) {
        throw new AppError(loginError);
      }

      /**
       * User was successfully created, now we can send a request for a new business
       * We can think about `journey`, but for now we will just register the new business from here
       */
      await dispatch(
        requestPolicyNewBusiness({
          product: "wysh-term-life",
        })
      ).unwrap();

      const nextScreenPath = "/create-phone";
      const callback = () => navigate(nextScreenPath, { replace: true });
      const nextStep = nextStepCallback(callback, false);
      nextStep();

      createApplicationReferral();
    } catch (err) {
      setFormError(AppError.from(err).fullMessage);
    } finally {
      form.setLoading(false);
    }
  };

  const emailInput = inputs["email"];
  const passwordInput = inputs["password"];
  const joinNewsletterInput = inputs["joinNewsletter"];

  const displayError = t(formError, formError);

  const passwordInputError = form.getInputError(passwordInput.id);
  const passwordInputErrors = useMemo(() => {
    if (!passwordInputError) {
      return [];
    }

    const errors = form.getInputErrorMessage(passwordInput.id);
    const validation = formValidations["validatePasswordLength"];
    return errors.filter(error => error !== validation.defaultError);
  }, [passwordInputError, form, passwordInput.id]);

  const passwordInputHint = useMemo<ComponentProps<typeof PasswordInput>["hint"]>(() => {
    const validations = passwordInput.validations;
    const validationName = "validatePasswordLength";
    const passwordLengthValidation = validations.find(
      validation => (validation as { name?: string })?.name === validationName
    );
    if (!passwordLengthValidation) {
      return { enabled: false };
    }

    const successStatus =
      !passwordInputError && passwordInput.dirty && passwordInput.touched && !passwordFocused;

    const status = successStatus ? "success" : passwordInputError ? "error" : undefined;
    return { enabled: true, status };
  }, [
    passwordFocused,
    passwordInput.dirty,
    passwordInput.touched,
    passwordInput.validations,
    passwordInputError,
  ]);

  const handlePasswordFocus = () => {
    if (!passwordFocused) {
      setPasswordFocused(true);
    }
  };

  const handlePasswordBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (passwordFocused) {
      setPasswordFocused(false);
    }

    form.onBlur(event);
  };

  return (
    <Layout as="TwoColumnLayout" content={content} backNav={{ hasBackNav: false }}>
      <div className="create-account">
        <form noValidate onSubmit={handleSubmit}>
          <EmailInput
            id={emailInput.id}
            value={emailInput.value as string}
            onChange={form.onChange}
            onBlur={form.onBlur}
            error={form.getInputError(emailInput.id)}
            errorMessage={form.getInputErrorMessage(emailInput.id)}
            label={t("register_user.form.email", "Email")}
            dataTestId="create-account__email"
          />
          <PasswordInput
            id={passwordInput.id}
            value={passwordInput.value as string}
            onChange={form.onChange}
            onFocus={handlePasswordFocus}
            onBlur={handlePasswordBlur}
            hint={passwordInputHint}
            error={form.getInputError(passwordInput.id)}
            errorMessage={passwordInputErrors}
            label={t("register_user.form.password", "Password")}
            dataTestId="create-account__password"
          />
          <Checkbox
            id={joinNewsletterInput.id}
            label={t(
              "register_user.checkbox",
              "Keep me up-to-date with all things Wysh. by joining the Newsletter!"
            )}
            value={joinNewsletterInput.value as string}
            onChange={form.onChange}
            checked={joinNewsletterInput.value as boolean}
            className="create-account__checkbox"
          />
          <div className="create-account__terms p6-tag__text">
            {t(
              "register_user.legal.v2",
              "By continuing, you attest that you are over 18 years old and agree to our "
            )}{" "}
            <DisclosureLink className="link p6-tag__text" slug="terms-conditions">
              {t("register_user.terms", "Terms & Conditions")}
            </DisclosureLink>{" "}
            {t("register_user.and", "and")}{" "}
            <DisclosureLink className="link p6-tag__text" slug="privacy-policy">
              {t("register_user.privacy_policy", "Privacy Policy")}
            </DisclosureLink>
          </div>
          {displayError && <FormError style={{ marginTop: 20 }} error={displayError} />}
          <Button
            id="submit"
            className="form__submit create-account__btn"
            onClick={() => form.validateAllFields()}
            disabled={form.getSubmitBtnDisabled()}
            isLoading={form.loading}
            subtype="primary"
            text={t("register_user.form.button", "CONFIRM INFO AND CONTINUE")}
            type="submit"
            dataTestId="create-account__submit"
            dataAnalyticsId={createAccountAnalyticsId}
          />
        </form>
      </div>
    </Layout>
  );
};

export default CreateAccount;
